import { useState, useEffect, useCallback } from 'react';

export type Theme = 'light' | 'dark' | 'mono-light' | 'citrus-light' | 'citrus-dark' | 'editorial';

const THEME_KEY = 'theme';
const VALID_THEMES: Theme[] = ['light', 'dark', 'mono-light', 'citrus-light', 'citrus-dark', 'editorial'];

function getSystemTheme(): Theme {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

function getStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored && VALID_THEMES.includes(stored as Theme)) {
      return stored as Theme;
    }
  } catch {
    // localStorage might be blocked
  }
  return null;
}

function getInitialTheme(): Theme {
  return getStoredTheme() ?? getSystemTheme();
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  // Apply theme to DOM
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // localStorage might be blocked
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't explicitly set a theme
      const stored = getStoredTheme();
      if (!stored) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState(current => {
      // Simple toggle between light and dark
      if (current === 'light' || current === 'mono-light' || current === 'citrus-light') {
        return 'dark';
      }
      return 'light';
    });
  }, []);

  const isDark = theme === 'dark' || theme === 'citrus-dark';

  return {
    theme,
    setTheme,
    toggleTheme,
    isDark,
    themes: VALID_THEMES,
  };
}
